import { uiText } from '@/i18n/interface';
import { supabase } from '@/integrations/supabase/client';

export const syncSubscriptionStatus = async (email: string) => {
  if (!email) {
    console.error('❌ Email is required for subscription sync');
    return { success: false, error: 'Email is required' };
  }

  console.log('🔄 Syncing admin Stripe subscription for email:', email);

  try {
    // First check if subscriptions table exists
    const { error: tableCheckError } = await supabase
      .from('subscriptions')
      .select('id')
      .limit(1);

    if (tableCheckError && tableCheckError.code === '42P01') {
      console.log('ℹ️ Subscriptions table does not exist, skipping subscription sync');
      return { success: true, message: uiText("Subscriptions table not available") };
    }

    // Get admin profile (only admins should have Stripe subscriptions)
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select(`
        *,
        dojos (
          id,
          name,
          plan,
          is_active
        )
      `)
      .ilike('email', email.trim().toLowerCase())
      .single();

    if (profileError) {
      console.error('❌ Error finding user profile:', profileError);
      return { success: false, error: profileError };
    }

    if (!profile) {
      console.error('❌ User profile not found for email:', email);
      return { success: false, error: 'User not found' };
    }

    console.log('👤 Found profile for:', profile.email, 'Role:', profile.role);

    // Only process if user is admin (students don't have Stripe subscriptions)
    if (profile.role !== 'admin' && profile.role !== 'superadmin') {
      console.log('ℹ️ User is not admin, skipping Stripe subscription sync');
      return { success: false, error: 'Only admins can have Stripe subscriptions' };
    }

    // Check for active Stripe subscription via API
    console.log('🔍 Checking Stripe for active subscription...');
    const { data: stripeData, error: stripeError } = await supabase.functions
      .invoke('check-stripe-subscription', {
        body: { email: email }
      });

    if (stripeError) {
      console.error('❌ Stripe check failed:', stripeError);
      return { success: false, error: 'Failed to check Stripe subscription' };
    }

    const hasActiveStripeSubscription = stripeData?.summary?.hasActiveSubscriptions || false;

    if (!hasActiveStripeSubscription) {
      console.log('❌ No active Stripe subscription found for admin');
      return {
        success: false,
        error: 'No active Stripe subscription found',
        paymentLink: 'https://buy.stripe.com/fZu5kD06o4U37Ha8WTdAk00'
      };
    }

    console.log('✅ Active Stripe subscription confirmed');

    // Check if subscription already exists in our database
    const { data: existingSubscription, error: subError } = await supabase
      .from('subscriptions')
      .select('*')
      .eq('user_id', profile.id)
      .eq('is_active', true)
      .eq('payment_status', 'paid') // Use consistent internal terminology
      .maybeSingle();

    if (subError) {
      console.error('❌ Error checking existing subscription:', subError);
      return { success: false, error: subError };
    }

    let subscriptionResult;

    if (existingSubscription) {
      console.log('✅ Active subscription already exists, updating...');

      // Update existing subscription with latest Stripe data
      const { data: updatedSubscription, error: updateError } = await supabase
        .from('subscriptions')
        .update({
          payment_status: 'paid',
          updated_at: new Date().toISOString()
        })
        .eq('id', existingSubscription.id)
        .select()
        .single();

      if (updateError) {
        console.error('❌ Error updating subscription:', updateError);
        return { success: false, error: updateError };
      }

      subscriptionResult = updatedSubscription;
    } else {
      console.log('🆕 Creating new subscription based on Stripe data...');

      // Create new subscription record
      const subscriptionData = {
        user_id: profile.id,
        dojo_id: profile.dojo_id,
        stripe_subscription_id: stripeData?.stripe?.subscriptions?.[0]?.id || null,
        stripe_customer_id: stripeData?.stripe?.customers?.[0]?.id || null,
        plan_type: 'monthly',
        is_active: true,
        payment_status: 'paid', // Use consistent internal terminology
        current_period_start: new Date().toISOString(),
        current_period_end: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(), // 30 days from now
      };

      const { data: newSubscription, error: createError } = await supabase
        .from('subscriptions')
        .insert(subscriptionData)
        .select()
        .single();

      if (createError) {
        console.error('❌ Error creating subscription:', createError);
        return { success: false, error: createError };
      }

      subscriptionResult = newSubscription;
      console.log('✅ Created new subscription:', newSubscription.id);
    }

    // Update user payment status to 'complete' for cross-dojo access
    await supabase
      .from('profiles')
      .update({ payment_status: 'complete' })
      .eq('id', profile.id);

    // Activate the admin's dojo with pro plan
    if (profile.dojo_id) {
      await supabase
        .from('dojos')
        .update({
          plan: 'pro',
          is_active: true,
          updated_at: new Date().toISOString()
        })
        .eq('id', profile.dojo_id)
        .neq('name', 'Taiga Dojo'); // Preserve Taiga Dojo

      console.log('🏢 Activated dojo pro plan for admin user');
    }

    return { success: true, subscription: subscriptionResult };

  } catch (error: any) {
    console.error('❌ Error in admin subscription sync:', error);
    return { success: false, error: error };
  }
};

const syncSubscriptionByEmail = async (email: string) => {
  try {
    console.log('🔄 Syncing admin Stripe subscription for email:', email);

    // Get admin profile (only admins should have Stripe subscriptions)
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('id, dojo_id, role')
      .ilike('email', email.trim().toLowerCase())
      .single();

    if (profileError || !profile) {
      console.error('❌ User not found:', email);
      return false;
    }

    // Only process if user is admin (students don't have Stripe subscriptions)
    if (profile.role !== 'admin' && profile.role !== 'superadmin') {
      console.log('ℹ️ User is not admin, skipping Stripe subscription sync');
      return false;
    }

    // Check for active Stripe subscription
    const { data: subscription, error: subError } = await supabase
      .from('subscriptions')
      .select('*')
      .eq('user_id', profile.id)
      .eq('is_active', true)
      .eq('payment_status', 'paid') // Use consistent internal terminology
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (subError) {
      console.error('❌ Error checking Stripe subscription:', subError);
      return false;
    }

    if (subscription && profile.dojo_id) {
      // Check if subscription is still valid
      const endDate = new Date(subscription.current_period_end);
      const isValid = endDate > new Date();

      if (isValid) {
        // Update dojo to paid plan
        const { error: dojoError } = await supabase
          .from('dojos')
          .update({
            plan: 'pro',
            is_active: true,
            updated_at: new Date().toISOString()
          })
          .eq('id', profile.dojo_id)
          .neq('name', 'Taiga Dojo'); // Preserve Taiga Dojo

        if (dojoError) {
          console.error('❌ Error updating dojo plan:', dojoError);
          return false;
        }

        console.log('✅ Dojo plan synced to pro based on admin Stripe subscription');
        return true;
      }
    }

    console.log('ℹ️ No active admin Stripe subscription found');
    return false;
  } catch (error) {
    console.error('❌ Error in syncSubscriptionByEmail:', error);
    return false;
  }
};

const syncSubscriptionByDojo = async (dojoId: string) => {
  try {
    console.log('🔄 Syncing subscription for dojo:', dojoId);

    // Check for active subscription
    const { data: subscription, error: subError } = await supabase
      .from('subscriptions')
      .select('*')
      .eq('dojo_id', dojoId)
      .eq('is_active', true)
      .eq('payment_status', 'paid') // Use consistent internal terminology
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (subError) {
      console.error('❌ Error checking subscription:', subError);
      return false;
    }

    if (subscription) {
      // Check if subscription is still valid
      const endDate = new Date(subscription.current_period_end);
      const isValid = endDate > new Date();

      if (isValid) {
        // Update dojo to paid plan
        const { error: dojoError } = await supabase
          .from('dojos')
          .update({
            plan: 'pro',
            is_active: true,
            updated_at: new Date().toISOString()
          })
          .eq('id', dojoId)
          .neq('name', 'Taiga Dojo'); // Preserve Taiga Dojo

        if (dojoError) {
          console.error('❌ Error updating dojo plan:', dojoError);
          return false;
        }

        console.log('✅ Dojo plan synced to pro');
        return true;
      }
    }

    console.log('ℹ️ No active subscription found');
    return false;
  } catch (error) {
    console.error('❌ Error in syncSubscriptionByDojo:', error);
    return false;
  }
};

// Auto-sync function that runs periodically
export const autoSyncSubscriptions = async () => {
  console.log('🔄 Running automatic subscription sync...');

  try {
    // First check if subscriptions table exists
    const { error: tableCheckError } = await supabase
      .from('subscriptions')
      .select('id')
      .limit(1);

    if (tableCheckError && tableCheckError.code === '42P01') {
      console.log('ℹ️ Subscriptions table does not exist, skipping auto-sync');
      return { success: true, message: uiText("Subscriptions table not available") };
    }

    // Get all admin profiles with active dojos
    const { data: adminProfiles, error: profilesError } = await supabase
      .from('profiles')
      .select(`
        id,
        email,
        role,
        dojo_id,
        dojos!inner(id, plan, is_active)
      `)
      .eq('role', 'admin')
      .eq('dojos.is_active', true);

    if (profilesError) {
      console.error('❌ Error fetching admin profiles:', profilesError);
      return { success: false, error: profilesError };
    }

    if (!adminProfiles || adminProfiles.length === 0) {
      console.log('ℹ️ No dojos found for sync');
      return { success: true, message: uiText("No dojos to sync") };
    }

    console.log(`🎯 Found ${adminProfiles.length} admin profiles to check for subscription sync`);

    let syncedCount = 0;
    const results = [];

    for (const profile of adminProfiles) {
      console.log('🔄 Syncing subscription for dojo:', profile.dojo_id);

      const result = await syncSubscriptionStatus(profile.email);
      results.push({ dojo: profile.dojo_id, result });

      if (result.success) {
        syncedCount++;
      }
    }

    console.log(`✅ Auto-sync completed. Synced ${syncedCount} subscriptions`);
    return {
      success: true,
      message: uiText("Synced {{value0}} of {{value1}} dojos", { value0: syncedCount, value1: adminProfiles.length }),
      results
    };

  } catch (error: any) {
    console.error('❌ Error in auto-sync:', error);
    return { success: false, error: error };
  }
};

export const manuallyActivateSubscription = async (email: string) => {
  console.log('🔧 Manually activating subscription for:', email);

  try {
    // Call the database function to create/activate subscription
    const { error } = await supabase.rpc('create-subscription-checkout', {
      p_user_email: email
    });

    if (error) {
      console.error('❌ Error in manual activation:', error);
      throw error;
    }

    console.log('✅ Manual activation successful');
    return { success: true };
  } catch (error: any) {
    console.error('❌ Manual activation failed:', error);
    return { success: false, error: error.message };
  }
};